import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ToggleOption, DateOption, DateRange } from '../types'

const LABEL_KEYS: Record<string, string> = {
  'All': 'common.all',
  'Single': 'inbox.single',
  'Multi': 'feed.filter.multi',
  'Yes': 'common.yes',
  'No': 'common.no',
  'Today': 'common.today',
  'Yesterday': 'common.yesterday',
  '-2 days': 'feed.filter.minus2Days',
  '-3 days': 'feed.filter.minus3Days',
  'All dates': 'feed.filter.allDates',
}

interface FilterSectionProps {
  title: string
  direction?: 'row' | 'column'
  children: React.ReactNode
}

interface ToggleGroupProps {
  options: ToggleOption[]
  selected: ToggleOption
  onSelect: (option: ToggleOption) => void
}

interface DateGridProps {
  options: DateOption[]
  selected: DateRange
  onSelect: (dateRange: any) => void
  createDateRange: (label: string) => DateRange
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  direction = 'column',
  children
}) => (
  <Container direction={direction}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </Container>
)

export const ToggleGroup: React.FC<ToggleGroupProps> = ({
  options,
  selected,
  onSelect
}) => {
  const { t } = useTranslation()
  return (
    <ToggleContainer>
      {options.map(option => (
        <ToggleButton
          key={option.id}
          selected={selected.id === option.id}
          onClick={() => onSelect(option)}
        >
          {LABEL_KEYS[option.label] ? t(LABEL_KEYS[option.label]) : option.label}
        </ToggleButton>
      ))}
    </ToggleContainer>
  )
}

export const DateGrid: React.FC<DateGridProps> = ({
  options,
  selected,
  onSelect,
  createDateRange
}) => {
  const { t } = useTranslation()
  return (
    <DateGridContainer>
      {options.map(date => (
        <DateButton
          key={date.id}
          selected={selected.label === date.label}
          onClick={() => onSelect(createDateRange(date.label))}
        >
          <DateLabel>{LABEL_KEYS[date.label] ? t(LABEL_KEYS[date.label]) : date.label}</DateLabel>
          <DateSubtext>{LABEL_KEYS[date.date] ? t(LABEL_KEYS[date.date]) : date.date}</DateSubtext>
        </DateButton>
      ))}
    </DateGridContainer>
  )
}

const Container = styled.div<{direction?: string }>`
  display: flex;
  flex-direction: ${props => props.direction ?? 'column'};
  justify-content: space-between;
  gap: 12px;
`

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
`

const ToggleContainer = styled.div`
  display: flex;
  gap: 4px;
`

const ToggleButton = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: 8px 16px;
  backdrop-filter: blur(20px);
  border: 2px solid ${props => props.selected ? 'var(--bg-nonary)' : 'transparent'};
  background: ${props => props.selected ? 'var(--bg-nonary)' : 'var(--bg-primary)'};
  color: ${props => props.selected ? '#fff' : 'var(--text-primary)'};
  font-size: 12px;
  font-weight: 600;
  border-radius: 46px;
  cursor: pointer;
  transition: all 0.2s ease;
`

const DateGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  
  @media (min-width: 500px) {
    grid-template-columns: repeat(5, 1fr);
  }
`

const DateButton = styled.button<{ selected: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6px 0px;
  border: 2px solid ${props => props.selected ? '#48C0AE' : '#EEF3F8'};
  background: ${props => props.selected ? '#F1FFFF' : 'white'};
  backdrop-filter: blur(20px);
  border-radius: 46px;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (min-width: 500px) {
      padding: 6px 16px;
  }
`

const DateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1F2937;
`

const DateSubtext = styled.span`
  font-size: 12px;
  color: #6B7280;
  margin-top: 2px;
`
